import LeadParamsType from 'types/Apis/LeadParams';
import LeadTrackingParams, { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import EnhancedLeadTrackingFields from 'types/Apis/EnhancedLeadTrackingFields';
import { GeocodeAttributes as GeocodeAttrs } from 'types/Geocode';
import eventLogger from './EventLogger';
import UtmTracking from '../types/UtmTracking';
import FirstVisitTracking from '../types/FirstVisitTracking';

type Geolocation = GeocodeAttrs;

export interface EmailAcceptance {
  search: boolean;
  price: boolean;
  marketing: boolean;
}

export interface ContactInfo {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  zip?: string;
  address?: string;
}

export default class LeadParams {
  contactInfo: ContactInfo;

  geolocation?: Geolocation;

  emailAcceptance: EmailAcceptance;

  firstVisitTracking: FirstVisitTracking;

  utmTracking: UtmTracking;

  isPaid: boolean;

  sourcePage: string;

  vin: string;

  partnerType: string;

  trackingParams: LeadTrackingParams;

  isRegional: boolean;

  enhancedLeadTrackingFields: EnhancedLeadTrackingFields | undefined;

  constructor({
    contactInfo,
    geolocation,
    firstVisitTracking,
    utmTracking,
    isPaid,
    sourcePage,
    vin,
    partnerType,
    trackingParams,
    isRegional,
    emailAcceptance,
    enhancedLeadTrackingFields,
  }: {
    contactInfo: ContactInfo;
    geolocation?: Geolocation;
    firstVisitTracking: FirstVisitTracking;
    utmTracking: UtmTracking;
    isPaid: boolean;
    sourcePage: LeadTrackingPageSource;
    vin: string;
    partnerType: string;
    trackingParams: LeadTrackingParams;
    isRegional: boolean;
    emailAcceptance: EmailAcceptance;
    enhancedLeadTrackingFields?: EnhancedLeadTrackingFields;
  }) {
    this.emailAcceptance = emailAcceptance;
    this.contactInfo = contactInfo;
    this.geolocation = geolocation;
    this.firstVisitTracking = firstVisitTracking;
    this.utmTracking = utmTracking;
    this.isPaid = isPaid;
    this.sourcePage = sourcePage;
    this.vin = vin;
    this.isRegional = isRegional;
    this.partnerType = partnerType;
    this.trackingParams = trackingParams;
    this.enhancedLeadTrackingFields = enhancedLeadTrackingFields;
  }

  toJSON(): LeadParamsType {
    const originalLeadParams = {
      lead: {
        vin: this.vin,
        partner_type: this.partnerType,
        name: this.contactInfo.name,
        email: this.contactInfo.email,
        phone: this.contactInfo.phone,
        zip: this.geolocation?.zip || this.contactInfo.zip,
        message: this.contactInfo.message,
        tracking_params: this.trackingParams,
        lat: this.geolocation?.latitude,
        lon: this.geolocation?.longitude,
        city: this.geolocation?.city,
        state: this.geolocation?.stateAbbreviation,
        client_guid_timestamp: eventLogger.clientGuidTimestamp,
        address: this.contactInfo.address,
        is_regional: this.isRegional,
        tracking_campaign: this.utmTracking.campaign,
        tracking_source: this.utmTracking.source,
        tracking_medium: this.utmTracking.medium,
        tracking_term: this.utmTracking.term,
        tracking_content: this.utmTracking.content,
        tracking_source_page: this.sourcePage,
      },
      is_paid: this.isPaid,
      financing_offers: false,
      tracking_params: this.trackingParams,
      first_visit_page: this.firstVisitTracking.page,
      first_visit_campaign: this.firstVisitTracking.campaign,
      first_visit_referrer: this.firstVisitTracking.referrer,
      accepts_search_emails: this.emailAcceptance.search,
      accepts_price_emails: this.emailAcceptance.price,
      accepts_marketing_emails: this.emailAcceptance.marketing,
    };

    return {
      ...originalLeadParams,
      ...this.enhancedLeadTrackingFields,
    };
  }
}
